import {
  Clock,
  AlertTriangle,
  CreditCard,
  CheckCircle,
  Loader2,
  Ban,
  FileText,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  usePaymentBatches,
  useApprovePaymentBatch,
  useProcessPaymentBatch,
  PaymentBatchCard,
  PaymentDetailModal,
} from '@/features/finance';
import type {
  PaymentBatch, PaymentStatus 
} from '@/features/finance';
import { toast } from 'sonner';
import {
  useMemo, useState 
} from 'react';

const PAYMENT_STATUS_TABS: {
  value: PaymentStatus | undefined;
  label: string;
  icon: typeof Clock;
}[] = [
  {
    value: undefined,
    label: 'All',
    icon: FileText,
  },
  {
    value: 'draft',
    label: 'Draft',
    icon: FileText,
  },
  {
    value: 'pending_approval',
    label: 'Pending',
    icon: Clock,
  },
  {
    value: 'approved',
    label: 'Approved',
    icon: CheckCircle,
  },
  {
    value: 'processing',
    label: 'Processing',
    icon: Loader2,
  },
  {
    value: 'completed',
    label: 'Completed',
    icon: CheckCircle,
  },
  {
    value: 'failed',
    label: 'Failed',
    icon: Ban,
  },
];

export function PaymentsView() {
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<PaymentStatus | undefined>(
    undefined
  );
  const [selectedBatch, setSelectedBatch] = useState<PaymentBatch | null>(null);

  const {
    data: paymentData,
    isLoading: isLoadingPayments,
    error: paymentError,
    refetch: refetchPayments,
  } = usePaymentBatches({ status: selectedPaymentStatus });
  const { data: allPaymentData } = usePaymentBatches();

  const approveMutation = useApprovePaymentBatch();
  const processMutation = useProcessPaymentBatch();

  const paymentBatches = paymentData?.data ?? [];

  const paymentStats = useMemo(() => {
    const batches = allPaymentData?.data ?? [];
    return {
      total: batches.length,
      draft: batches.filter((b) => b.status === 'draft').length,
      pendingApproval: batches.filter((b) => b.status === 'pending_approval').length,
      processing: batches.filter((b) => b.status === 'processing').length,
      completed: batches.filter((b) => b.status === 'completed').length,
      failed: batches.filter((b) => b.status === 'failed').length,
      totalPending: allPaymentData?.meta?.totalPending ?? 0,
      totalProcessing: allPaymentData?.meta?.totalProcessing ?? 0,
      totalCompleted: allPaymentData?.meta?.totalCompleted ?? 0,
    };
  }, [allPaymentData]);

  const handleApprovePayment = (batchId: string) => {
    approveMutation.mutate(batchId, {
      onSuccess: () => {
        toast.success('Payment batch approved');
        setSelectedBatch(null);
        refetchPayments();
      },
      onError: () => toast.error('Failed to approve payment batch'),
    });
  };

  const handleProcessPayment = (batchId: string) => {
    processMutation.mutate(batchId, {
      onSuccess: () => {
        toast.success('Payment batch submitted for processing');
        setSelectedBatch(null);
        refetchPayments();
      },
      onError: () => toast.error('Failed to process payment batch'),
    });
  };

  const handleRejectPayment = (batchId: string) => {
    toast.info(`Payment batch ${batchId} rejected`);
    setSelectedBatch(null);
  };

  const handleRetryPayment = (batchId: string) => {
    toast.info(`Retrying payment batch ${batchId}`);
    setSelectedBatch(null);
  };

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Approval
            </CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{paymentStats.pendingApproval}</div>
            <p className="text-xs text-muted-foreground">
              ${paymentStats.totalPending.toLocaleString()} total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Processing</CardTitle>
            <Loader2 className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{paymentStats.processing}</div>
            <p className="text-xs text-muted-foreground">
              ${paymentStats.totalProcessing.toLocaleString()} in transit
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Completed (MTD)
            </CardTitle>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{paymentStats.completed}</div>
            <p className="text-xs text-muted-foreground">
              ${paymentStats.totalCompleted.toLocaleString()} paid
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{paymentStats.failed}</div>
            <p className="text-xs text-muted-foreground">Requires attention</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {PAYMENT_STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={selectedPaymentStatus === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPaymentStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoadingPayments && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={`skel-pay-${i.toString()}`}
              className="h-72 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoadingPayments && paymentError && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load payment batches</p>
            <Button variant="outline" className="mt-4" onClick={() => refetchPayments()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoadingPayments && !paymentError && paymentBatches.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">No payment batches found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Create a payment batch from approved invoices
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoadingPayments && !paymentError && paymentBatches.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {paymentBatches.map((batch) => (
            <div
              key={batch.batchId}
              className="cursor-pointer"
              onClick={() => setSelectedBatch(batch)}
            >
              <PaymentBatchCard
                batch={batch}
                onApprove={handleApprovePayment}
                onProcess={handleProcessPayment}
                isApproving={approveMutation.isPending}
                isProcessing={processMutation.isPending}
              />
            </div>
          ))}
        </div>
      )}

      <PaymentDetailModal
        batch={selectedBatch}
        isOpen={selectedBatch !== null}
        onClose={() => setSelectedBatch(null)}
        onApprove={handleApprovePayment}
        onProcess={handleProcessPayment}
        onReject={handleRejectPayment}
        onRetry={handleRetryPayment}
        isApproving={approveMutation.isPending}
        isProcessing={processMutation.isPending}
      />
    </>
  );
}
